import { Ban, LoaderCircle } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { Locale } from '../../../domain/tools/types';
import { formatBytes, formatNumber } from '../../../lib/format';
import type { ProcessingProgressUpdate } from '../../../processing/progress';
import '../workspace.css';

export interface ProgressPanelProps {
  readonly progress: ProcessingProgressUpdate;
  readonly title?: string;
  readonly detail?: string;
  readonly canceling?: boolean;
  readonly cancelDisabled?: boolean;
  readonly onCancel?: () => void;
}

function localeFromLanguage(language: string): Locale {
  return language.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

export function ProgressPanel({
  progress,
  title,
  detail,
  canceling = false,
  cancelDisabled = false,
  onCancel,
}: ProgressPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = localeFromLanguage(i18n.resolvedLanguage ?? i18n.language);
  const titleId = useId();
  const detailId = `${titleId}-detail`;
  const counted = progress.kind === 'counted';
  const percent = counted ? Math.round((progress.completed / progress.total) * 100) : undefined;

  let progressDescription = detail;
  if (!progressDescription && counted) {
    if (progress.unit === 'pages') {
      progressDescription = t('workflow.processing.processingPage', {
        current: formatNumber(progress.completed, locale),
        total: formatNumber(progress.total, locale),
      });
    } else if (progress.unit === 'files') {
      progressDescription = t('workflow.processing.processingFileNumber', {
        current: formatNumber(progress.completed, locale),
        total: formatNumber(progress.total, locale),
      });
    } else if (progress.unit === 'bytes') {
      progressDescription = `${formatBytes(progress.completed, locale)} / ${formatBytes(progress.total, locale)}`;
    } else {
      progressDescription = `${formatNumber(progress.completed, locale)} / ${formatNumber(progress.total, locale)}`;
    }
  }

  return (
    <section className="workspace-progress" aria-labelledby={titleId} aria-describedby={detailId}>
      <span className="workspace-progress__icon" aria-hidden="true">
        <LoaderCircle className="workspace-spin" size={27} />
      </span>
      <div className="workspace-progress__body">
        <div className="workspace-progress__heading">
          <h2 id={titleId}>{title ?? t('workflow.processing.title')}</h2>
          {percent !== undefined && (
            <strong>{t('common.units.percent', { value: formatNumber(percent, locale) })}</strong>
          )}
        </div>
        {progressDescription && <p id={detailId}>{progressDescription}</p>}
        {!progressDescription && <p id={detailId}>{t('workflow.processing.keepOpen')}</p>}
        <div
          className="workspace-progress__track"
          data-indeterminate={!counted || undefined}
          role="progressbar"
          aria-label={t('a11y.progressLabel', { task: title ?? t('common.status.processing') })}
          aria-valuemin={counted ? 0 : undefined}
          aria-valuemax={counted ? 100 : undefined}
          aria-valuenow={percent}
          aria-valuetext={percent === undefined
            ? t('common.status.processing')
            : t('a11y.progressValue', { percent: formatNumber(percent, locale) })}
        >
          <span style={counted ? { inlineSize: `${percent}%` } : undefined} />
        </div>
      </div>
      {onCancel && (
        <button
          className="workspace-secondary-button"
          type="button"
          disabled={cancelDisabled || canceling}
          onClick={onCancel}
        >
          <Ban size={17} aria-hidden="true" />
          {canceling ? t('common.status.canceling') : t('workflow.processing.cancel')}
        </button>
      )}
    </section>
  );
}
